/* eslint-disable */
// Generated from voia-travel-mobile app/utils/projectPins.ts (types stripped). Keep in sync: edit there, re-run the transpile.
// Pin projection layer.
//
// The trip's pin spine is moving from a "dates-first" model (every pin stores
// absolute checkInDate/checkOutDate, trip startDate/endDate are the source of
// truth) to an "ordered-list-first" model (pins are an ordered list with
// durations; trip dates are derived from a single anchor).
//
// This file is the seam: a canonical Pin shape, a lossless adapter that reads
// today's stored pins into that shape, and a pure projection that walks the
// list to produce concrete dates. New writers can target the canonical shape
// directly; legacy data flows through the adapter unchanged.
import { FLEXIBLE_TRIP_ANCHOR } from './tripDates.js';
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE;
const toDate = (v) => {
    if (v == null)
        return null;
    if (v instanceof Date)
        return isNaN(v.getTime()) ? null : v;
    if (typeof (v === null || v === void 0 ? void 0 : v.toDate) === 'function') {
        const d = v.toDate();
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    const parsed = new Date(v);
    return isNaN(parsed.getTime()) ? null : parsed;
};
const isSpineType = (t) => t === 'destination' || t === 'dayVisit' || t === 'transit';
// Stored start/end vary by type: destination/dayVisit use checkInDate/checkOutDate,
// transit uses startDate/endDate.
const legacyStart = (p) => p.type === 'transit' ? toDate(p.startDate) : toDate(p.checkInDate);
const legacyEnd = (p) => p.type === 'transit' ? toDate(p.endDate) : toDate(p.checkOutDate);
export function legacyToPins(trip) {
    const raw = Array.isArray(trip === null || trip === void 0 ? void 0 : trip.pins) ? trip.pins : [];
    const spine = raw.filter((p) => p && isSpineType(p.type));
    const sorted = [...spine].sort((a, b) => {
        var _a, _b;
        const da = (_a = legacyStart(a)) === null || _a === void 0 ? void 0 : _a.getTime();
        const db = (_b = legacyStart(b)) === null || _b === void 0 ? void 0 : _b.getTime();
        // Pins missing a start sort to the end deterministically.
        if (da == null && db == null)
            return 0;
        if (da == null)
            return 1;
        if (db == null)
            return -1;
        return da - db;
    });
    return sorted.map((p, idx) => {
        const start = legacyStart(p);
        const end = legacyEnd(p);
        // Prefer the canonical `durationNights` field if the pin carries it
        // (new write-path). Fall back to date-difference math for legacy pins
        // that only store checkIn/checkOut.
        const storedDuration = typeof p.durationNights === 'number' && p.durationNights >= 0
            ? p.durationNights
            : null;
        const computedDuration = start && end
            ? Math.max(0, Math.round((end.getTime() - start.getTime()) / MS_PER_DAY))
            : 0;
        const durationNights = storedDuration !== null && storedDuration !== void 0 ? storedDuration : computedDuration;
        return {
            id: p.id,
            order: idx + 1,
            type: p.type,
            durationNights,
            // Treat every legacy pin's stored start as an absolute clock-time pin.
            // This is what makes projection lossless against today's data: the
            // computedStart equals the stored start, byte for byte.
            pinnedStart: start
                ? { kind: 'absolute', utc: start.toISOString() }
                : undefined,
            legacy: p,
        };
    });
}
// Project the day-visits attached to a trip onto concrete dates by walking
// each visit's host in `pinProjection`. Handles two shapes:
//   • New shape: `trip.dayVisits[]` carries explicit { parentPinId, dayOffset }.
//   • Legacy shape: dayVisit-typed entries inside `trip.pins[]` carry a
//     stored `checkInDate`; the host is inferred as whichever spine pin
//     (stay/transit) projection range contains that date.
function projectDayVisits(trip, pinProjection) {
    const out = [];
    const hostsById = new Map();
    pinProjection.forEach((p) => {
        if (p.type !== 'dayVisit')
            hostsById.set(p.id, p);
    });
    // The last non-dayVisit pin is the trip's terminal host. Its checkout day
    // (dayOffset === durationNights) is the trip's final day — a valid slot for
    // a day-visit, even though it's "outside" the host's nights. Other hosts
    // can't use that offset (their checkout day belongs to the next host).
    let lastSpineHostId = null;
    for (let i = pinProjection.length - 1; i >= 0; i -= 1) {
        if (pinProjection[i].type !== 'dayVisit') {
            lastSpineHostId = pinProjection[i].id;
            break;
        }
    }
    // 1. New-shape day visits.
    const newVisits = Array.isArray(trip === null || trip === void 0 ? void 0 : trip.dayVisits) ? trip.dayVisits : [];
    newVisits.forEach((dv) => {
        if (!dv || typeof dv.parentPinId !== 'string' || typeof dv.dayOffset !== 'number')
            return;
        const host = hostsById.get(dv.parentPinId);
        if (!host) {
            out.push({
                id: dv.id,
                name: dv.name,
                placeId: dv.placeId,
                latitude: dv.latitude,
                longitude: dv.longitude,
                photoUrl: dv.photoUrl,
                parentPinId: dv.parentPinId,
                dayOffset: dv.dayOffset,
                createdAt: dv.createdAt,
                legacy: dv,
                computedDay: new Date(FLEXIBLE_TRIP_ANCHOR),
                isOrphan: true,
            });
            return;
        }
        const computedDay = new Date(host.computedStart.getTime() + dv.dayOffset * MS_PER_DAY);
        const isLastHost = host.id === lastSpineHostId;
        // dayOffset === durationNights is the host's checkout day. It's only
        // valid (non-orphan) when host is the LAST spine pin — otherwise that
        // calendar day is owned by the next host.
        const isOrphan = dv.dayOffset < 0
            || dv.dayOffset > host.durationNights
            || (dv.dayOffset === host.durationNights && !isLastHost);
        out.push({
            id: dv.id,
            name: dv.name,
            placeId: dv.placeId,
            latitude: dv.latitude,
            longitude: dv.longitude,
            photoUrl: dv.photoUrl,
            parentPinId: host.id,
            dayOffset: dv.dayOffset,
            createdAt: dv.createdAt,
            legacy: dv,
            computedDay,
            isOrphan,
        });
    });
    // 2. Legacy-shape: dayVisit-typed pins in `pins[]` with a stored checkInDate.
    // Infer host by which spine pin's projected range contains that date.
    const rawPins = Array.isArray(trip === null || trip === void 0 ? void 0 : trip.pins) ? trip.pins : [];
    rawPins.forEach((p) => {
        if (!p || p.type !== 'dayVisit')
            return;
        // Skip entries already present via the new-shape array (matched by id).
        if (newVisits.some((dv) => (dv === null || dv === void 0 ? void 0 : dv.id) === p.id))
            return;
        const dvDate = toDate(p.checkInDate);
        if (!dvDate)
            return;
        const dvMs = dvDate.getTime();
        let chosenHost = null;
        for (const host of pinProjection) {
            if (host.type === 'dayVisit')
                continue;
            const startMs = host.computedStart.getTime();
            const endMs = host.computedEnd.getTime();
            if (dvMs >= startMs && dvMs < endMs) {
                chosenHost = host;
                break;
            }
        }
        if (!chosenHost) {
            out.push({
                id: p.id,
                name: p.name,
                placeId: p.placeId,
                latitude: p.latitude,
                longitude: p.longitude,
                photoUrl: p.photoUrl,
                parentPinId: '',
                dayOffset: 0,
                createdAt: p.createdAt,
                legacy: p,
                computedDay: dvDate,
                isOrphan: true,
            });
            return;
        }
        const dayOffset = Math.max(0, Math.floor((dvMs - chosenHost.computedStart.getTime()) / MS_PER_DAY));
        out.push({
            id: p.id,
            name: p.name,
            placeId: p.placeId,
            latitude: p.latitude,
            longitude: p.longitude,
            photoUrl: p.photoUrl,
            parentPinId: chosenHost.id,
            dayOffset,
            createdAt: p.createdAt,
            legacy: p,
            computedDay: dvDate,
            isOrphan: false,
        });
    });
    return out;
}
export function projectPins(trip) {
    var _a, _b, _c, _d;
    if (!trip) {
        return { pins: [], dayVisits: [], effectiveStart: null, effectiveEnd: null, totalDurationNights: 0 };
    }
    const ordered = legacyToPins(trip);
    // Anchor resolution: explicit trip.anchor.utc wins; otherwise fall back to
    // trip.startDate. Flexible trips have startDate = FLEXIBLE_TRIP_ANCHOR, which
    // is fine — projection still produces valid Date objects in year 1900, and
    // existing UI (formatTripDate) renders those as "Day N".
    const anchorUtc = (trip.anchor && toDate(trip.anchor.utc)) ||
        toDate(trip.startDate) ||
        null;
    let cursor = anchorUtc;
    const projected = ordered.map((p) => {
        var _a, _b, _c;
        // A legacy day-visit pin (dated, 0 nights) is not a stop: it keeps its own date but must not
        // move the cursor. Otherwise a dated day visit sorted ahead of undated stays dragged them onto
        // its day (Sintra on 6 Dec pushing a 5 Dec Lisbon stay to the 6th).
        if (p.type === 'dayVisit') {
            const own = ((_a = p.pinnedStart) === null || _a === void 0 ? void 0 : _a.kind) === 'absolute' ? new Date(p.pinnedStart.utc) : cursor !== null && cursor !== void 0 ? cursor : new Date(FLEXIBLE_TRIP_ANCHOR);
            return { ...p, computedStart: own, computedEnd: new Date(own.getTime() + p.durationNights * MS_PER_DAY) };
        }
        let start;
        if (((_b = p.pinnedStart) === null || _b === void 0 ? void 0 : _b.kind) === 'absolute') {
            start = new Date(p.pinnedStart.utc);
        }
        else if (((_c = p.pinnedStart) === null || _c === void 0 ? void 0 : _c.kind) === 'time-of-day' && cursor) {
            const c = new Date(cursor.getTime());
            c.setUTCHours(0, 0, 0, 0);
            start = new Date(c.getTime() + p.pinnedStart.minutesFromMidnight * MS_PER_MINUTE);
        }
        else if (cursor) {
            start = cursor;
        }
        else {
            // No anchor, no pinnedStart — best-effort fallback to FLEXIBLE_TRIP_ANCHOR
            // so callers still get a Date and not null. Real-world legacy data
            // always has either a stored start or a trip.startDate, so this branch
            // is mostly a defensive no-op.
            start = new Date(FLEXIBLE_TRIP_ANCHOR);
        }
        const end = new Date(start.getTime() + p.durationNights * MS_PER_DAY);
        cursor = end;
        return { ...p, computedStart: start, computedEnd: end };
    });
    // Trip bounds come from the stops; day-visit pins only count when there is nothing else.
    const stops = projected.filter((p) => p.type !== 'dayVisit');
    const bounds = stops.length > 0 ? stops : projected;
    const effectiveStart = (_b = (_a = bounds[0]) === null || _a === void 0 ? void 0 : _a.computedStart) !== null && _b !== void 0 ? _b : null;
    const effectiveEnd = (_d = (_c = bounds[bounds.length - 1]) === null || _c === void 0 ? void 0 : _c.computedEnd) !== null && _d !== void 0 ? _d : null;
    const totalDurationNights = bounds.reduce((sum, p) => sum + p.durationNights, 0);
    const dayVisits = projectDayVisits(trip, projected);
    return { pins: projected, dayVisits, effectiveStart, effectiveEnd, totalDurationNights };
}
export const projectionInternals = {
    toDate,
    legacyStart,
    legacyEnd,
    isSpineType,
    MS_PER_MINUTE,
    MS_PER_DAY,
};
